import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Handshake, MapPin, Wallet, MoreVertical, Pencil, Archive } from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { MatchScore, OwnerBadge, Reasons, PropertyMeta, Fade, priceLabel } from "@/components/proptech";
import { SmartMatchDialog } from "@/components/SmartMatchDialog";
import { api, apiError, peso, track } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { propertyTypeLabel } from "@/lib/taxonomy";

const CLASS_STYLE = { STRONG: "bg-emerald-100 text-emerald-700", POSSIBLE: "bg-amber-100 text-amber-700" };
const classLabel = (c) => (c === "STRONG" ? "Strong Match" : c === "POSSIBLE" ? "Possible Match" : "");
const ClassBadge = ({ c }) => (classLabel(c) ? <span data-testid="class-badge" className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${CLASS_STYLE[c]}`}>{classLabel(c)}</span> : null);

export default function BuyerMatches() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmArchive, setConfirmArchive] = useState(false);

  useEffect(() => { api.get(`/matches/for-buyer/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, [id]);
  useEffect(() => { track("match_view", { buyer_requirement_id: id }); }, [id]);

  const setStatus = async (status) => {
    try { await api.patch(`/buyer-requirements/${id}/status`, { status }); toast.success(`Marked ${status}`); const r = await api.get(`/matches/for-buyer/${id}`); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doArchive = async () => {
    setConfirmArchive(false);
    try { await api.delete(`/buyer-requirements/${id}`); toast.success("Buyer Need archived"); nav("/my-buyers"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const collaborate = async (prop) => {
    setBusy(prop.id);
    try {
      const { data: c } = await api.post("/collaborations", {
        property_id: prop.id, buyer_requirement_id: id,
        note: `Hi! I have a buyer (${data.buyer.buyer_alias}) that could be a great fit for your listing "${prop.title}". Can we co-broke this?`,
      });
      toast.success("Collaboration room opened!");
      nav(`/collaboration/${c.id}`);
    } catch (e) { toast.error(apiError(e)); setBusy(""); }
  };

  if (!data) return <PageShell testid="buyer-matches-page"><p className="text-slate-500">Loading matches…</p></PageShell>;
  const b = data.buyer;

  return (
    <PageShell testid="buyer-matches-page">
      <button onClick={() => nav(-1)} data-testid="back-btn" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>
      <Fade>
        <div className="bg-primary text-primary-foreground rounded-2xl p-6 sm:p-7 mb-8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-accent font-semibold mb-1">Buyer Need</p>
              <h1 className="font-display text-2xl sm:text-3xl font-semibold">{b.buyer_alias}</h1>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="buyer-owner-actions" className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center shrink-0"><MoreVertical className="h-4 w-4" /></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="buyer-edit" onClick={() => nav(`/have-buyer?edit=${id}`)} className="gap-2"><Pencil className="h-4 w-4" /> Edit Buyer Need</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="buyer-pause" onClick={() => setStatus("Paused")}>Pause</DropdownMenuItem>
                <DropdownMenuItem data-testid="buyer-fulfill" onClick={() => setStatus("Fulfilled")}>Mark Fulfilled</DropdownMenuItem>
                <DropdownMenuItem data-testid="buyer-reactivate" onClick={() => setStatus("Active")}>Reactivate</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="buyer-archive" onClick={() => setConfirmArchive(true)} className="gap-2 text-red-600"><Archive className="h-4 w-4" /> Archive</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-slate-300 text-sm">
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{b.cities.join(", ")}{b.neighborhoods?.length ? ` · ${b.neighborhoods.join(", ")}` : ""}</span>
            {b.listing_type === "Unit Hosting" ? (
              <>
                <span className="flex items-center gap-1.5"><Wallet className="h-4 w-4" />{(b.nightly_budget_min || b.nightly_budget_max) ? `${peso(b.nightly_budget_min)}–${peso(b.nightly_budget_max)} / night` : "Nightly budget flexible"}</span>
                <span>{propertyTypeLabel(b.property_type)} · Stay{b.stay_guests > 0 ? ` · ${b.stay_guests} guest(s)` : ""}{b.stay_beds_min > 0 ? ` · ${b.stay_beds_min}+ bed(s)` : ""}</span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-1.5"><Wallet className="h-4 w-4" />{peso(b.budget_min)}–{peso(b.budget_max)}</span>
                <span>{propertyTypeLabel(b.property_type)} · {b.listing_type} · {b.min_bedrooms}+ BR</span>
              </>
            )}
          </div>
        </div>
      </Fade>
      <h2 className="font-display text-xl font-semibold text-slate-900 mb-4">{data.results.length} matching {data.results.length === 1 ? "property" : "properties"}{(data.strong_count || data.possible_count) ? <span className="text-slate-400 font-normal"> · {data.strong_count} Strong · {data.possible_count} Possible</span> : null}</h2>
      <div className="space-y-4" data-testid="match-results">
        {data.results.length === 0 && <p className="text-slate-500">No strong matches yet. Try widening the budget or cities.</p>}
        {data.results.map((p, i) => (
          <Fade key={p.id} delay={0.05 * i}>
            <div data-testid={`match-card-${p.id}`} className="bg-card border border-border rounded-xl overflow-hidden md:flex hover:shadow-md transition-shadow">
              <div className="md:w-64 h-44 md:h-auto shrink-0"><img src={p.image} alt="" className="h-full w-full object-cover" /></div>
              <div className="flex-1 min-w-0 p-5 flex flex-col sm:flex-row gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1"><span className="text-[11px] font-semibold uppercase tracking-wide bg-secondary text-slate-600 px-2 py-0.5 rounded">{p.listing_type}</span><span className="text-[11px] text-slate-400">{propertyTypeLabel(p.property_type)}</span></div>
                  <h3 className="font-display text-lg font-semibold text-slate-900 truncate">{p.title}</h3>
                  <div className="mt-1"><PropertyMeta p={p} /></div>
                  <div className="font-display text-xl font-bold text-slate-900 mt-2">{priceLabel(p)}</div>
                  <div className="mt-3"><Reasons match={p.match} /></div>
                  <div className="mt-3 pt-3 border-t border-border"><OwnerBadge owner={p.owner} small /></div>
                </div>
                <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:justify-between shrink-0">
                  <div className="flex flex-col items-center sm:items-end gap-1.5">
                    <MatchScore score={p.match.score} />
                    <ClassBadge c={p.classification} />
                  </div>
                  {p.is_own ? (
                    <div className="flex flex-col items-end gap-1.5">
                      <span data-testid={`own-property-${p.id}`} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Your Property</span>
                      <Button data-testid={`view-property-${p.id}`} onClick={() => nav(`/property/${p.id}`)} variant="outline" className="rounded-xl font-semibold whitespace-nowrap">View Property</Button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap justify-end gap-2">
                      <SmartMatchDialog testid={`why-${p.id}`} buyerId={id} propertyId={p.id} onCollaborate={() => collaborate(p)} busy={busy === p.id} />
                      <Button data-testid={`collaborate-${p.id}`} onClick={() => collaborate(p)} disabled={busy === p.id} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl font-semibold active:scale-95 transition-transform whitespace-nowrap shrink-0"><Handshake className="h-4 w-4 mr-1.5" /> {busy === p.id ? "Opening…" : "Collaborate"}</Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Fade>
        ))}
      </div>
      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent data-testid="buyer-archive-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this Buyer Need?</AlertDialogTitle>
            <AlertDialogDescription>Archiving removes it from active matching. This is a soft-archive — the Buyer Need and its historical collaborations are preserved and can be reviewed later. Continue?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="buyer-archive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="buyer-archive-ok" onClick={doArchive} className="bg-red-600 hover:bg-red-700">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}
